import React from 'react';
import { Card } from '@/components/ui/Card';
import { LeadSourceData } from '@/types/dashboard.types';
import { LeadSource } from '@/types/lead.types';

interface LeadSourcePieChartProps {
  data: LeadSourceData[];
  isLoading?: boolean;
}

// Color mapping for each lead source
const sourceColors: Record<LeadSource, { bg: string; fill: string; text: string }> = {
  WEBSITE: { bg: 'bg-blue-500', fill: '#3b82f6', text: 'text-blue-600' },
  INSTAGRAM: { bg: 'bg-pink-500', fill: '#ec4899', text: 'text-pink-600' },
  META: { bg: 'bg-indigo-500', fill: '#6366f1', text: 'text-indigo-600' },
  GOOGLE: { bg: 'bg-red-500', fill: '#ef4444', text: 'text-red-600' },
  EXHIBITION: { bg: 'bg-amber-500', fill: '#f59e0b', text: 'text-amber-600' },
  MANUAL: { bg: 'bg-gray-500', fill: '#6b7280', text: 'text-gray-600' },
  REFERRAL: { bg: 'bg-green-500', fill: '#22c55e', text: 'text-green-600' },
};

// User-friendly labels for sources
const sourceLabels: Record<LeadSource, string> = {
  WEBSITE: 'Website',
  INSTAGRAM: 'Instagram',
  META: 'Meta Ads',
  GOOGLE: 'Google Ads',
  EXHIBITION: 'Exhibition',
  MANUAL: 'Manual',
  REFERRAL: 'Referral',
};

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="h-6 bg-secondary-200 rounded w-36 mb-6"></div>
    <div className="flex items-center gap-6">
      <div className="w-40 h-40 rounded-full bg-secondary-100"></div>
      <div className="flex-1 space-y-3">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-secondary-200"></div>
            <div className="h-4 bg-secondary-200 rounded flex-1"></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

interface PieSlice {
  source: LeadSource;
  percentage: number;
  startAngle: number;
  endAngle: number;
  color: string;
}

const calculateSlices = (data: LeadSourceData[]): PieSlice[] => {
  const slices: PieSlice[] = [];
  let currentAngle = -90; // Start from top

  data.forEach((item) => {
    const angle = (item.percentage / 100) * 360;
    slices.push({
      source: item.source,
      percentage: item.percentage,
      startAngle: currentAngle,
      endAngle: currentAngle + angle,
      color: sourceColors[item.source]?.fill || '#6b7280',
    });
    currentAngle += angle;
  });

  return slices;
};

const describeArc = (
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return [
    'M', x, y,
    'L', start.x, start.y,
    'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y,
    'Z',
  ].join(' ');
};

const polarToCartesian = (
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
) => {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
};

export const LeadSourcePieChart: React.FC<LeadSourcePieChartProps> = ({
  data,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <Card className="h-full">
        <LoadingSkeleton />
      </Card>
    );
  }

  const totalLeads = data.reduce((sum, d) => sum + d.count, 0);
  const slices = calculateSlices(data);

  // Sort data by count for legend
  const sortedData = [...data].sort((a, b) => b.count - a.count);

  return (
    <Card className="h-full">
      <h3 className="text-lg font-semibold text-secondary-900 mb-4">Lead Sources</h3>

      {data.length === 0 ? (
        <div className="flex items-center justify-center h-48 bg-secondary-50 rounded-lg">
          <p className="text-secondary-500">No data available</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row items-center gap-6">
          {/* Pie Chart */}
          <div className="relative w-40 h-40 flex-shrink-0">
            <svg viewBox="0 0 100 100" className="w-full h-full">
              {slices.map((slice, index) => (
                <path
                  key={index}
                  d={describeArc(50, 50, 45, slice.startAngle, slice.endAngle - 0.5)}
                  fill={slice.color}
                  className="transition-all duration-200 hover:opacity-80 cursor-pointer"
                  style={{ transformOrigin: '50px 50px' }}
                >
                  <title>
                    {sourceLabels[slice.source]}: {slice.percentage.toFixed(1)}%
                  </title>
                </path>
              ))}
              {/* Center circle for donut effect */}
              <circle cx="50" cy="50" r="25" fill="white" />
              {/* Center text */}
              <text
                x="50"
                y="47"
                textAnchor="middle"
                className="text-lg font-bold"
                fill="#1f2937"
                fontSize="12"
              >
                {totalLeads}
              </text>
              <text
                x="50"
                y="58"
                textAnchor="middle"
                fill="#6b7280"
                fontSize="6"
              >
                Total Leads
              </text>
            </svg>
          </div>

          {/* Legend */}
          <div className="flex-1 space-y-2 w-full">
            {sortedData.map((item) => {
              const colors = sourceColors[item.source] || { bg: 'bg-gray-500', text: 'text-gray-600' };
              return (
                <div
                  key={item.source}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-secondary-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${colors.bg}`}></div>
                    <span className="text-sm text-secondary-700">
                      {sourceLabels[item.source]}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-secondary-900">
                      {item.count}
                    </span>
                    <span className={`text-xs font-medium ${colors.text}`}>
                      {item.percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
};

export default LeadSourcePieChart;
